import { custoConfig, montarParametros } from '@favo/core';
import { ArrowRight } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { Celula } from '@/componentes/celula';
import { Badge } from '@/componentes/ui/badge';
import { RodapeFunil } from '@/componentes/rodape-funil';
import { Button } from '@/componentes/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/componentes/ui/card';
import { Skeleton } from '@/componentes/ui/skeleton';
import { RotuloValor, Valor } from '@/componentes/valor';
import type {
  BaseIncidencia,
  ChaveCustoDireto,
  ConfigCusto,
  Integrador,
  ModoCusto,
  Parametros,
  Projeto,
} from '@/dados';
import { mensagemDoErro, useIntegrador, useProjetoAtual } from '@/dados';
import { formatarMoeda, formatarNumero, formatarPercentual, parseNum } from '@/design/formato';
import { Pagina } from '@/layout/pagina';
import { cn } from '@/lib/utils';

/* indicadores.tsx — a conferência do que foi cadastrado, antes de precificar.
 *
 * Tela de LEITURA. Nenhum campo, nenhum salvamento: o usuário revê custos,
 * despesas e meta com os mesmos números que o motor vai usar na etapa
 * seguinte. Por isso todo valor sai de `montarParametros` / `custoConfig`, e
 * não de conta local: se a tela somasse por conta própria, ela poderia dizer
 * uma coisa e o preço sugerido outra.
 *
 * Custo derivado (por kWp, por módulo, por faixa) sem projeto carregado NÃO
 * vira número: `custoConfig` devolve null e a linha mostra a REGRA. Inventar
 * um valor aqui seria pior que faltar, porque o usuário confiaria nele.
 */

const ORDEM_CUSTO: readonly ChaveCustoDireto[] = ['ca', 'inst', 'proj', 'homol', 'art'];

const ROTULO_CUSTO: Record<ChaveCustoDireto, string> = {
  ca: 'Material adicional CA',
  inst: 'Instalação',
  proj: 'Projeto elétrico',
  homol: 'Homologação',
  art: 'ART do projeto',
};

/* O modo de cobrança vive na NOTA, à esquerda: assim a coluna da direita fica
   só de algarismos, alinhados e comparáveis de cima a baixo. */
const ROTULO_MODO: Record<ModoCusto, string> = {
  fixo: 'Valor fixo',
  modulo: 'Por módulo',
  kwp: 'Por kWp',
  faixa: 'Escalonado por faixa',
};

/* Cortes de saúde da despesa fixa sobre o faturamento, em FRAÇÃO (0,2 = 20%).
   Fração porque é o que sai da divisão e o que `formatarPercentual` espera;
   guardar em pontos aqui abriria a porta para o erro de 100×. */
const FAIXAS_PESO = [
  { ate: 0.2, rotulo: 'Saudável', tom: 'positivo', barra: 'bg-positivo', selo: 'positivo' },
  { ate: 0.3, rotulo: 'Atenção', tom: 'atencao', barra: 'bg-atencao', selo: 'atencao' },
  { ate: Infinity, rotulo: 'Crítico', tom: 'negativo', barra: 'bg-negativo', selo: 'negativo' },
] as const;

/** Referência desenhada no trilho do medidor e citada na legenda. */
const REFERENCIA_PESO = FAIXAS_PESO[0].ate;

export function Indicadores() {
  const integrador = useIntegrador();
  const projetoAtual = useProjetoAtual();

  return (
    <Pagina
      titulo="Indicadores"
      descricao="Resumo do que já foi cadastrado. Revise antes de precificar o projeto."
      etapa="indicadores"
    >
      {integrador.isPending || projetoAtual.isPending ? (
        <Carregando />
      ) : integrador.isError ? (
        <Falha
          mensagem={mensagemDoErro(integrador.error)}
          aoTentar={() => void integrador.refetch()}
        />
      ) : (
        <Conferencia
          integrador={integrador.data ?? {}}
          projeto={projetoAtual.data ?? null}
        />
      )}
    </Pagina>
  );
}

function Conferencia({
  integrador,
  projeto,
}: {
  integrador: Integrador;
  projeto: Projeto | null;
}) {
  const p = React.useMemo(
    () => montarParametros({ integrador, projeto }),
    [integrador, projeto],
  );

  const despesas = integrador.despesas ?? {};
  const comissaoInformada = informado(despesas.comissao?.valor);
  const tributoInformado = informado(despesas.tributo?.valor);

  return (
    <div className="favo">
      <Pendencias
        p={p}
        variaveisInformadas={comissaoInformada || tributoInformado}
      />

      <BlocoMeta p={p} />

      <div className="favo items-start @coluna-larga:grid-cols-2">
        <BlocoCustos p={p} integrador={integrador} />
        <BlocoVariaveis
          p={p}
          comissaoInformada={comissaoInformada}
          tributoInformado={tributoInformado}
        />
      </div>

      <BlocoFixas p={p} />

      <RodapeFunil etapa="indicadores">
        <Button asChild>
          <Link to="/projeto">
            Continuar <ArrowRight />
          </Link>
        </Button>
      </RodapeFunil>
    </div>
  );
}

/* ── Bloco 1: meta, volume e ticket ──────────────────────────────────────── */

function BlocoMeta({ p }: { p: Parametros }) {
  return (
    <Bloco
      titulo="Meta e operação"
      descricao="A margem que precisa sobrar em cada projeto, e o volume sobre o qual ela é calculada."
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
        <div className="min-w-0">
          <p className="text-corpo font-medium">Margem de lucro líquida alvo</p>
          <p className="apoio-nota">
            {p.lucroAlvoDefinido
              ? 'Definida na etapa Empresa.'
              : 'Não definida. O cálculo usa 12% como padrão.'}
          </p>
        </div>
        {/* lucroAlvo vem em PONTOS (12 = 12%): formato percentual-pontos. */}
        <Valor valor={p.lucroAlvo} formato="percentual-pontos" tamanho="display" />
      </div>

      {/* Volume e ticket moram aqui, ao lado da meta, porque são os números
          que o alerta de pendências cobra — sem eles na tela, o usuário lê
          "volume não informado" sem ter onde conferir o que já existe. */}
      <div className="border-border mt-4 grid gap-4 border-t pt-4 @campos:grid-cols-2">
        <RotuloValor
          rotulo="Volume anual"
          valor={p.V12 > 0 ? p.V12 : null}
          formato="numero"
          sufixo={p.V12 > 0 ? 'projetos' : undefined}
          nota={notaDoVolume(p)}
        />
        <RotuloValor
          rotulo="Ticket médio"
          valor={p.ticket > 0 ? p.ticket : null}
          formato="moeda"
          nota={notaDoTicket(p)}
        />
      </div>
    </Bloco>
  );
}

/**
 * De onde saiu o volume anual.
 *
 * A marca de PROJEÇÃO pertence a este número, e não ao faturamento médio:
 * V12 é anualizado (soma ÷ cobertura × 12), então com histórico de seis meses
 * o volume do ano é estimado, não observado. O faturamento médio, por
 * oposição, é uma média medida sobre os meses que existem.
 */
function notaDoVolume(p: Parametros): string {
  if (!(p.V12 > 0)) return 'Preencha a Sazonalidade para calcular.';
  if (!p.temSazonalidade) return 'Vem de uma média mensal antiga. Informe o histórico em Sazonalidade.';
  if (p.coberturaSaz < 12) {
    return `Projeção a partir de ${mesesPorExtenso(p.coberturaSaz)} de histórico.`;
  }
  return 'Histórico dos doze meses da Sazonalidade.';
}

function notaDoTicket(p: Parametros): string {
  if (p.ticketOrigem === 'projeto') return 'Preço de venda do projeto atual.';
  if (p.ticketOrigem === 'sazonalidade') {
    return 'Média do histórico: faturamento dividido por projetos.';
  }
  return 'Informe o preço de venda em Projeto.';
}

function mesesPorExtenso(meses: number): string {
  return meses === 1 ? '1 mês' : `${meses} meses`;
}

/* ── Bloco 2: custos diretos ─────────────────────────────────────────────── */

function BlocoCustos({ p, integrador }: { p: Parametros; integrador: Integrador }) {
  const campos = integrador.configs?.campos ?? {};
  const extras = (integrador.configs?.extras ?? []).filter((e) => e?.label);

  const linhas: React.ReactNode[] = ORDEM_CUSTO.map((chave) => (
    <LinhaCusto
      key={chave}
      rotulo={ROTULO_CUSTO[chave]}
      cfg={campos[chave]}
      kwp={p.kwp}
      mods={p.mods}
    />
  ));

  extras.forEach((extra, i) => {
    linhas.push(
      <LinhaCusto
        key={`extra-${extra.id ?? i}`}
        rotulo={extra.label ?? ''}
        cfg={extra}
        kwp={p.kwp}
        mods={p.mods}
        sufixoNota="custo adicional"
      />,
    );
  });

  return (
    <Bloco
      titulo="Custos diretos"
      descricao={
        p.kwp > 0 || p.mods > 0
          ? `O que entra no custo de cada projeto, além do kit. Custo por medida usa o projeto atual: ${formatarNumero(p.kwp, { casasMax: 2 })} kWp e ${formatarNumero(p.mods, { casasMax: 0 })} módulos.`
          : 'O que entra no custo de cada projeto, além do kit.'
      }
    >
      <Lista>{linhas}</Lista>
    </Bloco>
  );
}

function LinhaCusto({
  rotulo,
  cfg,
  kwp,
  mods,
  sufixoNota,
}: {
  rotulo: string;
  cfg: ConfigCusto | undefined;
  kwp: number;
  mods: number;
  sufixoNota?: string;
}) {
  const modo = cfg?.modo;
  if (!cfg || !modo) {
    return (
      <Linha rotulo={rotulo} nota={sufixoNota}>
        <span className="text-muted-foreground text-apoio">Não configurado</span>
      </Linha>
    );
  }

  const nota = sufixoNota ? `${ROTULO_MODO[modo]} · ${sufixoNota}` : ROTULO_MODO[modo];
  const valor = custoConfig(cfg, kwp, mods);

  return (
    <Linha rotulo={rotulo} nota={nota}>
      {valor === null ? (
        // Derivado sem medidas do sistema: a regra configurada, nunca um
        // número. Texto em fonte normal para não fingir que é algarismo.
        <span className="text-muted-foreground text-apoio">{regraDoCusto(cfg)}</span>
      ) : (
        <Valor valor={valor} formato="moeda" />
      )}
    </Linha>
  );
}

/** Como a empresa cobra este custo, em uma linha. */
function regraDoCusto(cfg: ConfigCusto): string {
  if (cfg.modo === 'kwp') return `${formatarMoeda(parseNum(cfg.taxa))} por kWp`;
  if (cfg.modo === 'modulo') return `${formatarMoeda(parseNum(cfg.taxa))} por módulo`;
  if (cfg.modo === 'faixa') return 'Por faixa';
  return 'Não configurado';
}

/* ── Bloco 3: despesas variáveis ─────────────────────────────────────────── */

function BlocoVariaveis({
  p,
  comissaoInformada,
  tributoInformado,
}: {
  p: Parametros;
  comissaoInformada: boolean;
  tributoInformado: boolean;
}) {
  return (
    <Bloco
      titulo="Despesas variáveis"
      descricao="Percentuais que acompanham o volume de vendas."
    >
      <Lista>
        <Linha
          key="comissao"
          rotulo="Comissão de vendas"
          nota={comissaoInformada ? baseDe(p.comissaoBase) : 'Não informada'}
        >
          {comissaoInformada ? (
            // Percentual digitado pelo usuário: PONTOS.
            <Valor valor={p.comissao} formato="percentual-pontos" />
          ) : (
            <Valor valor={null} formato="percentual-pontos" tom="apagado" />
          )}
        </Linha>

        <Linha
          key="tributo"
          rotulo="Carga tributária"
          nota={tributoInformado ? baseDe(p.tributoBase) : 'Não informada'}
        >
          {tributoInformado ? (
            <Valor valor={p.tributo} formato="percentual-pontos" />
          ) : (
            <Valor valor={null} formato="percentual-pontos" tom="apagado" />
          )}
        </Linha>

        {p.despesasExtras.map((extra, i) => (
          <Linha key={`extra-${i}`} rotulo={extra.nome} nota={baseDe(extra.base)}>
            <Valor valor={extra.valor} formato="percentual-pontos" />
          </Linha>
        ))}

        {p.outras > 0 ? (
          <Linha
            key="outras"
            rotulo="Percentual adicional"
            nota="Sobre venda total"
          >
            <Valor valor={p.outras} formato="percentual-pontos" />
          </Linha>
        ) : null}
      </Lista>
    </Bloco>
  );
}

function baseDe(base: BaseIncidencia): string {
  return base === 'servico' ? 'Sobre serviços' : 'Sobre venda total';
}

/* ── Bloco 4: despesa fixa e o peso dela ─────────────────────────────────── */

function BlocoFixas({ p }: { p: Parametros }) {
  /* Faturamento médio mensal: soma do histórico dividida pelos meses que ele
     REALMENTE cobre. Dividir por 12 fixo subestima quem informou seis meses
     (a média cai pela metade), o que inflava o percentual e pintava
     "Crítico" em empresa saudável. */
  let totalFaturado = 0;
  if (p.meses) {
    for (const mes of Object.values(p.meses)) totalFaturado += parseNum(mes?.valor);
  }
  const mesesCobertos = p.temSazonalidade ? p.coberturaSaz : 12;
  const fatMedioMensal = totalFaturado / mesesCobertos;

  const peso = fatMedioMensal > 0 ? p.despFixa / fatMedioMensal : null;
  const faixa = peso === null ? null : (FAIXAS_PESO.find((f) => peso <= f.ate) ?? FAIXAS_PESO[2]);

  /* Histórico parcial: a média acima é MEDIDA sobre os meses informados, não
     projetada para o ano — quem é anualizado é o volume, e é lá que mora a
     marca de projeção. A ressalva vai na NOTA, ao lado de onde o número
     nasce, e nunca colada no próprio número. */
  const cobertura =
    p.temSazonalidade && p.coberturaSaz < 12
      ? ` Média de ${mesesPorExtenso(p.coberturaSaz)} de histórico.`
      : '';

  return (
    <Bloco
      titulo="Despesas fixas"
      descricao="Custo mensal da estrutura, independente do volume de projetos."
    >
      <Lista>
        <Linha
          key="fixa"
          rotulo="Despesa fixa mensal"
          nota="Total cadastrado na etapa Despesas"
        >
          <Valor valor={p.despFixa} formato="moeda" />
        </Linha>

        {peso === null || faixa === null ? (
          <Linha
            key="peso"
            rotulo="Peso sobre o faturamento médio"
            nota="Informe o faturamento por mês em Sazonalidade para calcular"
          >
            <Valor valor={null} formato="percentual" tom="apagado" />
          </Linha>
        ) : (
          <Linha
            key="peso"
            rotulo="Peso sobre o faturamento médio"
            nota={`Faturamento médio de ${formatarMoeda(fatMedioMensal)} por mês.${cobertura}`}
          >
            {/* peso é FRAÇÃO (despFixa ÷ faturamento): formato percentual. */}
            <Valor valor={peso} formato="percentual" tom={faixa.tom} />
          </Linha>
        )}
      </Lista>

      {peso !== null && faixa !== null ? (
        <Medidor peso={peso} faixa={faixa} />
      ) : null}
    </Bloco>
  );
}

function Medidor({
  peso,
  faixa,
}: {
  peso: number;
  faixa: (typeof FAIXAS_PESO)[number];
}) {
  return (
    <div className="pt-4">
      <div
        className="bg-muted relative h-2 overflow-hidden rounded-full"
        role="img"
        aria-label={`Despesa fixa em ${formatarPercentual(peso)} do faturamento médio: ${faixa.rotulo}`}
      >
        <div
          className={cn('h-full rounded-full transition-[width] duration-300', faixa.barra)}
          style={{ width: `${Math.min(peso * 100, 100).toFixed(1)}%` }}
        />
        {/* Entalhe na referência: sem uma marca, a barra chapada de ponta a
            ponta se lê como barra de rolagem. */}
        <div
          className="bg-card absolute inset-y-0 w-0.5"
          style={{ left: `${REFERENCIA_PESO * 100}%` }}
        />
      </div>
      <div className="text-muted-foreground mt-3 flex flex-wrap items-center gap-2 text-nota">
        <Badge variant={faixa.selo}>{faixa.rotulo}</Badge>
        <span>Referência: até {formatarPercentual(REFERENCIA_PESO, 0)} do faturamento médio.</span>
      </div>
    </div>
  );
}

/* ── Pendências ──────────────────────────────────────────────────────────── */

function Pendencias({
  p,
  variaveisInformadas,
}: {
  p: Parametros;
  variaveisInformadas: boolean;
}) {
  // Cada pendência aponta a etapa que a resolve: nome da tela sem link só
  // transfere o problema para o usuário procurar no menu.
  const itens: Array<{ texto: string; etapa: string; rota: string }> = [];

  if (!p.lucroAlvoDefinido) {
    itens.push({
      texto: 'Margem de lucro líquida alvo não definida.',
      etapa: 'Empresa',
      rota: '/empresa',
    });
  }
  if (!(p.despFixa > 0)) {
    itens.push({ texto: 'Despesa fixa mensal zerada.', etapa: 'Despesas', rota: '/despesas' });
  }
  if (!variaveisInformadas) {
    itens.push({
      texto: 'Comissão e carga tributária não informadas.',
      etapa: 'Despesas',
      rota: '/despesas',
    });
  }
  if (!(p.V12 > 0)) {
    itens.push({
      texto: 'Volume de projetos não informado.',
      etapa: 'Sazonalidade',
      rota: '/sazonalidade',
    });
  }

  if (itens.length === 0) return null;

  return (
    <Celula role="alert" respiro="justo" className="bg-atencao-tinta">
      <p className="text-corpo font-semibold">Falta preencher</p>
      <ul className="mt-2 grid gap-1 text-apoio">
        {itens.map((item) => (
          <li key={`${item.rota}-${item.texto}`}>
            {item.texto}{' '}
            <Link to={item.rota} className="font-medium underline underline-offset-2">
              Ir para {item.etapa}
            </Link>
          </li>
        ))}
      </ul>
    </Celula>
  );
}

/* ── Peças de layout desta tela ──────────────────────────────────────────── */

function Bloco({
  titulo,
  descricao,
  children,
}: {
  titulo: string;
  descricao: string;
  children: React.ReactNode;
}) {
  return (
    <Card className="gap-4">
      <CardHeader>
        <CardTitle>{titulo}</CardTitle>
        <CardDescription>{descricao}</CardDescription>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

/** Lista de leitura: rótulo à esquerda, valor à direita, régua entre linhas. */
function Lista({ children }: { children: React.ReactNode }) {
  return <div className="divide-border grid divide-y">{children}</div>;
}

function Linha({
  rotulo,
  nota,
  children,
}: {
  rotulo: string;
  nota?: string | undefined;
  children: React.ReactNode;
}) {
  return (
    // flex-wrap é a rede de segurança: valor longo desce para a linha de
    // baixo em vez de vazar do cartão. min-w-0 nos dois lados porque item de
    // flex nasce com min-width auto e se recusa a encolher.
    <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1 py-3">
      <div className="min-w-0 flex-1">
        <p className="text-corpo font-medium [overflow-wrap:anywhere]">{rotulo}</p>
        {nota ? <p className="apoio-nota">{nota}</p> : null}
      </div>
      <div className="ml-auto min-w-0 shrink-0 text-right">{children}</div>
    </div>
  );
}

/** True quando o usuário informou o percentual. `0` é informação; vazio não. */
function informado(valor: number | string | null | undefined): boolean {
  return valor !== null && valor !== undefined && valor !== '';
}

function Carregando() {
  return (
    <div className="favo">
      {[0, 1, 2].map((i) => (
        <Card key={i} className="gap-4">
          <CardHeader>
            <Skeleton className="h-4 w-48" />
          </CardHeader>
          <CardContent className="grid gap-3">
            <Skeleton className="h-5 w-full" />
            <Skeleton className="h-5 w-4/5" />
            <Skeleton className="h-5 w-3/5" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/* Telha é o campo da reprova, e falha de carregamento é reprova em todas as
   dez telas. Célula de nata aqui fazia a mesma situação ter duas caras
   conforme a tela em que o usuário estivesse. */
function Falha({ mensagem, aoTentar }: { mensagem: string; aoTentar: () => void }) {
  return (
    <Celula campo="telha" respiro="folgado" role="alert" className="grid justify-items-start gap-3">
      <p className="titulo-bloco">Não foi possível carregar os indicadores</p>
      <p className="apoio">{mensagem}</p>
      <Button variant="outline" onClick={aoTentar}>
        Tentar de novo
      </Button>
    </Celula>
  );
}
